import React, { useMemo, useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { Assert } from '@cotera/utilities';
import { useUntenantedClient } from '@cotera/client/app/stores/org';
import { TimeInterval } from '@cotera/api';
import { BasicTable } from '../../../components/basic-table';
import { OrgDetailDrawer } from './org-detail-drawer';
import { useOrgNames } from '../hooks/use-org-names';
import { atcQueryKeys } from '../query-keys';
import { isOrgUuid } from '../utils/org-id';

interface OrgRow {
  orgId: string;
  organizationName: string;
  total: number;
  successCount: number;
  failureCount: number;
  activeCount: number;
}

interface SystemServiceRow {
  service: string;
  total: number;
  successCount: number;
  failureCount: number;
}

interface ActiveInvocationsGroupedProps {
  timeInterval: TimeInterval;
}

export const ActiveInvocationsGrouped: React.FC<
  ActiveInvocationsGroupedProps
> = ({ timeInterval }) => {
  const client = useUntenantedClient();
  const [drawerTarget, setDrawerTarget] = useState<{
    orgId: string;
    orgName: string;
    activeCount: number;
  } | null>(null);

  const { data: invocationData } = useSuspenseQuery({
    queryFn: async () => {
      const res = await client.atcVisibility.getInvocationSummary({
        timeInterval,
      });
      return Assert.assertOk(res);
    },
    queryKey: atcQueryKeys.invocationSummary(timeInterval),
  });

  const { data: activeData } = useSuspenseQuery({
    queryFn: async () => {
      const res = await client.atcVisibility.getActiveInvocationsByOrg({});
      return Assert.assertOk(res);
    },
    queryKey: atcQueryKeys.activeInvocationsByOrg(),
  });

  const orgIds = useMemo(
    () => [...new Set(invocationData.map((row) => row.org))],
    [invocationData]
  );

  const orgMap = useOrgNames(orgIds);

  const activeMap = useMemo(
    () => new Map(activeData.map((a) => [a.org, a.activeCount])),
    [activeData]
  );

  const data = useMemo<OrgRow[]>(() => {
    const byOrg = new Map<
      string,
      { total: number; successCount: number; failureCount: number }
    >();
    for (const row of invocationData) {
      if (!isOrgUuid(row.org)) {
        continue;
      }
      const existing = byOrg.get(row.org) ?? {
        total: 0,
        successCount: 0,
        failureCount: 0,
      };
      byOrg.set(row.org, {
        total: existing.total + row.total,
        successCount: existing.successCount + row.successCount,
        failureCount: existing.failureCount + row.failureCount,
      });
    }
    return [...byOrg.entries()].map(([orgId, counts]) => {
      const activeCount = activeMap.get(orgId) ?? 0;
      return {
        orgId,
        organizationName: orgMap.get(orgId) ?? orgId,
        ...counts,
        total: counts.total + activeCount,
        activeCount,
      };
    });
  }, [invocationData, orgMap, activeMap]);

  // Jobs/sagas whose target key doesn't begin with an org id (cron, locks,
  // dead-letter, system-scoped sagas, etc.). The extracted "org" is garbled
  // per-invocation, so group these by service instead.
  const systemData = useMemo<SystemServiceRow[]>(() => {
    const byService = new Map<
      string,
      { total: number; successCount: number; failureCount: number }
    >();
    for (const row of invocationData) {
      if (isOrgUuid(row.org)) {
        continue;
      }
      const existing = byService.get(row.service) ?? {
        total: 0,
        successCount: 0,
        failureCount: 0,
      };
      byService.set(row.service, {
        total: existing.total + row.total,
        successCount: existing.successCount + row.successCount,
        failureCount: existing.failureCount + row.failureCount,
      });
    }
    return [...byService.entries()]
      .map(([service, counts]) => ({ service, ...counts }))
      .sort((a, b) => b.total - a.total);
  }, [invocationData]);

  const columns = useMemo<ColumnDef<OrgRow>[]>(
    () => [
      {
        accessorKey: 'organizationName',
        header: 'Organization',
        cell: (info) => (
          <span className="font-medium text-gray-900">
            {info.getValue() as string}
          </span>
        ),
        enableColumnFilter: true,
      },
      {
        accessorKey: 'successCount',
        header: 'Success',
        cell: (info) => (
          <span className="text-green-600">{info.getValue() as number}</span>
        ),
        enableColumnFilter: false,
      },
      {
        accessorKey: 'failureCount',
        header: 'Failures',
        cell: (info) => {
          const value = info.getValue() as number;
          return value > 0 ? (
            <span className="font-semibold text-red-600">{value}</span>
          ) : (
            <span>{value}</span>
          );
        },
        enableColumnFilter: false,
      },
      {
        accessorKey: 'total',
        header: 'Total',
        cell: (info) => (
          <span className="font-medium">{info.getValue() as number}</span>
        ),
        enableColumnFilter: false,
      },
      {
        accessorKey: 'activeCount',
        header: 'Active',
        cell: (info) => {
          const value = info.getValue() as number;
          return value > 0 ? (
            <span className="font-semibold text-amber-600">{value}</span>
          ) : (
            <span className="text-gray-400">{value}</span>
          );
        },
        enableColumnFilter: false,
      },
    ],
    []
  );

  const systemColumns = useMemo<ColumnDef<SystemServiceRow>[]>(
    () => [
      {
        accessorKey: 'service',
        header: 'Service',
        cell: (info) => (
          <span className="font-mono text-xs text-gray-700">
            {info.getValue() as string}
          </span>
        ),
        enableColumnFilter: true,
      },
      {
        accessorKey: 'successCount',
        header: 'Success',
        cell: (info) => (
          <span className="text-green-600">{info.getValue() as number}</span>
        ),
        enableColumnFilter: false,
      },
      {
        accessorKey: 'failureCount',
        header: 'Failures',
        cell: (info) => {
          const value = info.getValue() as number;
          return value > 0 ? (
            <span className="font-semibold text-red-600">{value}</span>
          ) : (
            <span>{value}</span>
          );
        },
        enableColumnFilter: false,
      },
      {
        accessorKey: 'total',
        header: 'Total',
        cell: (info) => (
          <span className="font-medium">{info.getValue() as number}</span>
        ),
        enableColumnFilter: false,
      },
    ],
    []
  );

  const handleRowClick = (row: OrgRow) => {
    setDrawerTarget({
      orgId: row.orgId,
      orgName: row.organizationName,
      activeCount: row.activeCount,
    });
  };

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-700">
          Invocations by Organization
        </h3>
        <p className="text-xs text-gray-500">
          Click a row to view details and manage invocations
        </p>
      </div>
      <BasicTable
        data={data}
        columns={columns}
        enableSorting={true}
        enableFiltering={true}
        enablePagination={true}
        pageSize={10}
        onRowClick={handleRowClick}
      />

      <OrgDetailDrawer
        orgId={drawerTarget?.orgId ?? null}
        orgName={drawerTarget?.orgName ?? ''}
        activeCount={drawerTarget?.activeCount ?? 0}
        timeInterval={timeInterval}
        onClose={() => setDrawerTarget(null)}
      />

      {systemData.length > 0 && (
        <div className="space-y-3 pt-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-700">
              System &amp; Non-Org Jobs
            </h3>
            <p className="text-xs text-gray-500">
              Jobs and sagas not scoped to an organization, grouped by service
            </p>
          </div>
          <BasicTable
            data={systemData}
            columns={systemColumns}
            enableSorting={true}
            enableFiltering={true}
            enablePagination={true}
            pageSize={10}
          />
        </div>
      )}
    </div>
  );
};
